// src/pages/Tutorials.jsx
import { useState, useEffect, useCallback } from "react";
import { Search, BookOpen, Package, ShoppingCart, Copy, CheckCheck, X, AlertTriangle, Wallet, Shield, Users, Phone, Mail, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { toast } from "react-toastify";
import Currency from "../components/Currency";

// ── Category → icon map. Unrecognized categories render no icon at all
// (no emoji fallback) — that's intentional, not a bug.
function getCategoryIcon(category) {
  const cat = (category || "").toLowerCase();
  if (cat === "vpn")                          return Shield;
  if (cat === "social" || cat === "socials")  return Users;
  if (cat === "numbers" || cat === "number")  return Phone;
  if (cat === "email" || cat === "mail")      return Mail;
  if (cat === "web" || cat === "website")     return Globe;
  return null;
}

// ── Tutorial Card ─────────────────────────────────────────────────────────────
function TutorialCard({ tutorial, onBuy }) {
  const isOutOfStock = (tutorial.available ?? 0) === 0;
  const CategoryIcon = getCategoryIcon(tutorial.category);

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-xl border transition-all duration-200 ${
      isOutOfStock
        ? "border-gray-200 dark:border-gray-800 opacity-60"
        : "border-gray-200 dark:border-gray-800 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md"
    }`}>
      <div className="p-3 sm:p-4">
        {/* Icon + Title — icon only renders when we recognize the category */}
        <div className="flex items-start gap-2.5 sm:gap-3 mb-2 sm:mb-3">
          {CategoryIcon && (
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
              <CategoryIcon size={18} className="text-purple-600 dark:text-purple-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug break-words">
              {tutorial.title}
            </h3>
          </div>
        </div>

        {/* Description */}
        {tutorial.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed mb-2 sm:mb-3">
            {tutorial.description}
          </p>
        )}

        {/* Price + Buy — side by side, no separate stock line */}
        <div className="flex items-center justify-between gap-3 pt-1.5 sm:pt-2 border-t border-gray-100 dark:border-gray-800">
          <span className="text-base font-bold text-purple-600 dark:text-purple-400">
            <Currency amount={tutorial.priceNgn} />
          </span>
          <button
            onClick={() => onBuy(tutorial)}
            disabled={isOutOfStock}
            className="flex-shrink-0 flex items-center justify-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition"
          >
            <ShoppingCart size={12} />
            {isOutOfStock ? "Sold Out" : "Buy"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tutorial Purchase Modal ────────────────────────────────────────────────────
function TutorialPurchaseModal({ tutorial, walletBalance, onClose, onGoToWallet }) {
  const [purchasing,    setPurchasing]    = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [purchased,     setPurchased]     = useState(false);
  const [purchases,     setPurchases]     = useState([]);
  const [copied,        setCopied]        = useState({});
  const [quantity,      setQuantity]      = useState(1);

  const totalPrice   = tutorial.priceNgn * quantity;
  const insufficient = walletBalance < totalPrice;
  const shortfall    = totalPrice - walletBalance;
  const maxQty       = tutorial.available || 1;

  const handleConfirm = async () => {
    setPurchasing(true);
    setPurchaseError(null);
    try {
      const res = await api.post("/tutorials/buy", {
        groupKey:  tutorial.groupKey,
        quantity,
        unitPrice: tutorial.priceNgn,
        title:     tutorial.title,
        category:  tutorial.category,
      });
      if (res.data.success) {
        setPurchases(res.data.purchases || []);
        setPurchased(true);
        toast.success(`${quantity} tutorial(s) purchased!`);
      } else {
        setPurchaseError(res.data.message || "Purchase failed");
      }
    } catch (err) {
      setPurchaseError(err.response?.data?.message || "Purchase failed");
      toast.error(err.response?.data?.message || "Purchase failed");
    } finally {
      setPurchasing(false);
    }
  };

  const copyLine = (id, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(prev => ({ ...prev, [id]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [id]: false })), 2000);
    });
  };

  const copyAll = () => {
    const all = purchases.map(p => p.credentials?.email || "").join("\n");
    navigator.clipboard.writeText(all).then(() => {
      setCopied({ all: true });
      setTimeout(() => setCopied({}), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={!purchased ? onClose : undefined} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 dark:text-white text-base">
            {purchased ? "Purchase Complete" : "Buy Tutorial"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!purchased ? (
            <>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{tutorial.title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 capitalize">{tutorial.category}</p>

                {/* Quantity picker */}
                {maxQty > 1 && (
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Qty:</span>
                    <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <button onClick={() => setQuantity(q => Math.max(1, q - 1))} disabled={quantity <= 1}
                        className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 disabled:opacity-40 text-sm font-bold transition">−</button>
                      <span className="px-4 py-1.5 text-sm font-semibold text-gray-900 dark:text-white min-w-[40px] text-center">{quantity}</span>
                      <button onClick={() => setQuantity(q => Math.min(maxQty, q + 1))} disabled={quantity >= maxQty}
                        className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 disabled:opacity-40 text-sm font-bold transition">+</button>
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
                    <span className="text-base font-bold text-purple-600 dark:text-purple-400">
                      <Currency amount={totalPrice} />
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Your balance</span>
                    <span className={`text-sm font-medium ${insufficient ? "text-red-500" : "text-green-600 dark:text-green-400"}`}>
                      <Currency amount={walletBalance} />
                    </span>
                  </div>
                  {!insufficient && (
                    <div className="flex items-center justify-between pt-1.5 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-xs text-gray-500 dark:text-gray-400">After purchase</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        <Currency amount={walletBalance - totalPrice} />
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {insufficient && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-red-600 dark:text-red-400">Insufficient balance</p>
                      <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">
                        You need <span className="font-bold">₦{shortfall.toLocaleString()}</span> more.
                      </p>
                    </div>
                  </div>
                  <button onClick={onGoToWallet}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl transition">
                    <Wallet size={15} /> Fund Wallet Now
                  </button>
                </div>
              )}

              {purchaseError && (
                <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-4">
                  <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-500 dark:text-red-400">{purchaseError}</p>
                </div>
              )}

              <p className="text-xs text-gray-400 dark:text-gray-500 mb-5 leading-relaxed">
                ⚠️ All sales are final. Content will be revealed immediately after payment.
              </p>

              <div className="flex gap-3">
                <button onClick={onClose}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                  Cancel
                </button>
                <button onClick={handleConfirm} disabled={purchasing || insufficient}
                  className="flex-1 py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-xl transition flex items-center justify-center gap-2">
                  {purchasing
                    ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Processing...</>
                    : <><ShoppingCart size={15} />{purchaseError ? "Try Again" : "Confirm Buy"}</>
                  }
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-5">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCheck size={24} className="text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {purchases.length} tutorial{purchases.length !== 1 ? "s" : ""} purchased!
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Save your content below. Also visible in My Purchases.
                </p>
              </div>

              {purchases.length > 1 && (
                <button onClick={copyAll}
                  className="w-full flex items-center justify-center gap-2 py-2 mb-4 text-xs font-medium text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 transition">
                  {copied.all ? <CheckCheck size={13} className="text-green-500" /> : <Copy size={13} />}
                  {copied.all ? "Copied all!" : `Copy all ${purchases.length} items`}
                </button>
              )}

              <div className="space-y-3 mb-5">
                {purchases.map((p, index) => {
                  const content = p.credentials?.email || "No content available";
                  const id      = p._id || index;
                  return (
                    <div key={id} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Item {index + 1}
                        </span>
                        <button onClick={() => copyLine(id, content)}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition">
                          {copied[id] ? <CheckCheck size={12} className="text-green-500" /> : <Copy size={12} />}
                          {copied[id] ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <div className="bg-white dark:bg-gray-900 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
                        <p className="font-mono text-xs text-gray-800 dark:text-gray-200 break-all leading-relaxed">
                          {content}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button onClick={onClose}
                className="w-full py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition">
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Tutorials Page ────────────────────────────────────────────────────────
export default function Tutorials() {
  const navigate = useNavigate();

  const [tutorials,     setTutorials]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [showPurchases, setShowPurchases] = useState(false);
  const [myPurchases,   setMyPurchases]   = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [purchaseCopied, setPurchaseCopied] = useState({});

  const fetchTutorials = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/tutorials");
      if (res.data.success) setTutorials(res.data.tutorials || []);
    } catch {
      toast.error("Failed to load tutorials");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWallet = async () => {
    try {
      const res = await api.get("/wallet");
      setWalletBalance(res.data?.balance || 0);
    } catch {}
  };

  const fetchMyPurchases = async () => {
    setLoadingPurchases(true);
    try {
      const res = await api.get("/social/my-purchases");
      if (res.data.success) {
        // Only show tutorial purchases
        const tutorialPurchases = (res.data.purchases || []).filter(
          p => p.category === "tutorial" || p.meta?.type === "tutorial"
        );
        setMyPurchases(tutorialPurchases);
      }
    } catch {
      toast.error("Failed to load purchases");
    } finally {
      setLoadingPurchases(false);
    }
  };

  useEffect(() => {
    fetchTutorials();
    fetchWallet();
  }, [fetchTutorials]);

  const filtered = tutorials.filter(t => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      t.title?.toLowerCase().includes(term)       ||
      t.description?.toLowerCase().includes(term) ||
      t.category?.toLowerCase().includes(term)
    );
  });

  const copyPurchaseContent = (id, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setPurchaseCopied(prev => ({ ...prev, [id]: true }));
      setTimeout(() => setPurchaseCopied(prev => ({ ...prev, [id]: false })), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tutorials</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Browse and purchase verified tutorials and guides
            </p>
          </div>
          <button
            onClick={() => { setShowPurchases(true); fetchMyPurchases(); }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition">
            <Package size={16} /> My Purchases
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tutorials..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
         {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3 sm:p-4 animate-pulse">
              <div className="flex items-center gap-2.5 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="flex-1">
                  <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                </div>
              </div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-5/6" />
              <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded-lg w-20 ml-auto" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">
            {search.trim() ? `Nothing matched "${search}"` : "No tutorials available yet"}
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Check back later for new tutorials</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(tutorial => (
            <TutorialCard
              key={tutorial.groupKey}
              tutorial={tutorial}
              onBuy={t => setSelectedTutorial(t)}
            />
          ))}
        </div>
      )}

      {/* Purchase Modal */}
      {selectedTutorial && (
        <TutorialPurchaseModal
          tutorial={selectedTutorial}
          walletBalance={walletBalance}
          onClose={() => setSelectedTutorial(null)}
          onGoToWallet={() => { setSelectedTutorial(null); navigate("/dashboard/wallet"); }}
        />
      )}

      {/* My Purchases Drawer */}
      {showPurchases && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setShowPurchases(false)} />
          <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Package size={16} className="text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900 dark:text-white">My Tutorial Purchases</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{myPurchases.length} purchased</p>
                </div>
              </div>
              <button onClick={() => setShowPurchases(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {loadingPurchases ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : myPurchases.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                  <BookOpen size={40} className="text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No tutorial purchases yet</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">When you buy tutorials, they appear here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {myPurchases.map((p, index) => {
                    const content = p.credentials?.email || "No content available";
                    const id      = p._id || index;
                    return (
                      <div key={id} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-xs font-semibold text-gray-900 dark:text-white">{p.title}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {new Date(p.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                            </p>
                          </div>
                          <button onClick={() => copyPurchaseContent(id, content)}
                            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition ${
                              purchaseCopied[id]
                                ? "bg-green-100 dark:bg-green-900/30 text-green-600"
                                : "bg-white dark:bg-gray-900 text-purple-600 border border-purple-200 dark:border-purple-800"
                            }`}>
                            {purchaseCopied[id] ? <CheckCheck size={11} /> : <Copy size={11} />}
                            {purchaseCopied[id] ? "Copied!" : "Copy"}
                          </button>
                        </div>
                        <div className="bg-white dark:bg-gray-900 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
                          <p className="font-mono text-xs text-gray-800 dark:text-gray-200 break-all leading-relaxed">
                            {content}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}